import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  CreditCard, Coins, Crown, ShieldCheck, LogIn, Loader2, ArrowRight, X, Check, KeyRound, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { subscriptionsApi } from '@/lib/auth_api';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useAuth } from '@/lib/auth_context';
import { TierFeatureMap } from '@/components/admin/TierFeatureMap';

/** Global paywall modal (iter-17).
 *
 *  React-side rendering of the structured 402 payload backed by
 *  `backend/app/paywall.py:PaywallError`. The store (or any axios
 *  interceptor) calls `openPaywall(payload.paywall)`; we surface 3 tabs:
 *
 *    • Top up         — Stripe checkout via the matching pack ladder.
 *                       Detects wallet ('copyscape_tokens' vs default)
 *                       and routes to the right endpoint.
 *    • Subscribe      — (Phase B) hook into recurring price ids.
 *    • Sign in        — for guest users only (Emergent OAuth redirect).
 *
 *  Free-tier writing/editing/version-history continues to work; the
 *  modal only opens when a paid API call is invoked. */
/** Feb-2026 — Author vs Publisher savings banner with impression-tracking.
 *  Emits a `author_banner_impression` event exactly once per mount so the
 *  admin A/B CTR reads a clean impressions vs. clicks number. */
function ProPlusBanner({ saving, busy, onPick }) {
  React.useEffect(() => {
    try {
      fetch(`${(process.env.REACT_APP_BACKEND_URL || '') + '/api'}/analytics/paywall-event`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: 'author_banner_impression',
                                tier: 'author', surface: 'paywall' }),
      }).catch(() => {});
    } catch { /* ignore */ }
  }, []);
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={busy}
      className="flex w-full items-center gap-2 rounded-md border border-primary/50 bg-primary/5 p-3 text-left transition hover:border-primary hover:bg-primary/10 disabled:opacity-50"
      data-testid="paywall-pro-plus-banner"
    >
      <div className="flex h-8 w-8 flex-none items-center justify-center rounded-sm bg-primary/15 text-primary">
        <Crown className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-semibold">
          You could save <span className="text-primary">${saving}/mo</span> — try Author
        </div>
        <div className="mt-0.5 text-[10.5px] text-muted-foreground">
          Full AI toolkit for one book at a time — everything most writers need without the bulk-production layer.
        </div>
      </div>
      <ArrowRight className="h-3.5 w-3.5 flex-none text-primary" />
    </button>
  );
}

export default function PaywallDialog({ open, paywall, onClose }) {
  const { format } = useCurrency();
  const { user } = useAuth();
  const [tab, setTab] = useState('topup');
  const [busy, setBusy] = useState(false);
  const [cadence, setCadence] = useState('monthly');
  const [billingMode, setBillingMode] = useState('standard');   // "standard" | "byok"
  const [plans, setPlans] = useState([]);
  const [promo, setPromo] = useState({ enabled: false, label: '' });
  const [founding, setFounding] = useState({ active: false });

  useEffect(() => {
    if (!open) return;
    // If the paywall was triggered by a free-tier lifetime cap, open the
    // Subscribe tab immediately so the upgrade path is front-and-centre.
    const freeTierHit = paywall?.extra?.free_tier_upgrade;
    setTab(freeTierHit ? 'subscribe' : (paywall?.tab || (paywall?.guest ? 'signin' : 'topup')));
  }, [open, paywall?.guest, paywall?.tab, paywall?.extra?.free_tier_upgrade]);

  // Lazy-load subscription plans only when the Subscribe tab is opened.
  useEffect(() => {
    if (!open || tab !== 'subscribe' || plans.length) return;
    subscriptionsApi.plans()
      .then((r) => { setPlans(r.plans || []); setPromo(r.yearly_promo || { enabled: false, label: '' }); })
      .catch(() => toast.error('Could not load subscription plans.'));
    // 2026-10 — Founding Member launch coupon status (independent of the
    // yearly promo above). Never blocks the dialog if it fails to load.
    subscriptionsApi.foundingPromo().then(setFounding).catch(() => {});
  }, [open, tab, plans.length]);

  if (!paywall) return null;

  // God Mode / Admin users never have capability restrictions — show a brief
  // informational banner instead of the full paywall dialog.
  if (user?.is_god_mode) {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
        <DialogContent className="max-w-sm gap-0 p-0 overflow-hidden" data-testid="paywall-dialog-admin">
          <DialogHeader className="border-b border-border bg-gradient-to-r from-primary/15 via-primary/5 to-transparent px-5 py-4">
            <DialogTitle className="flex items-center gap-2 text-[15px] font-semibold">
              <ShieldAlert className="h-4 w-4 text-amber-500" />
              Admin account
            </DialogTitle>
          </DialogHeader>
          <div className="px-5 py-4 text-[13px] text-muted-foreground space-y-2">
            <p>Your admin account bypasses all spending caps and capability gates.</p>
            <p className="text-[11.5px]">If this paywall appeared in error, it has been suppressed. You may continue writing.</p>
          </div>
          <div className="border-t border-border px-5 py-3 flex justify-end">
            <Button size="sm" onClick={onClose} data-testid="paywall-admin-dismiss">
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isCopyscape = paywall.wallet === 'copyscape_tokens';
  const startCheckout = async (option) => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      // Copyscape packs use a dedicated endpoint that flags wallet='copyscape_tokens'
      // on the transaction; AI packs use the standard custom flow.
      const r = isCopyscape && option.label
        ? await api.paymentsApi.startCopyscapePack(option.id || `cs_pack_${option.tokens}`, origin)
        : await api.paymentsApi.startCustom(option.price_cents / 100, origin);
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
      setBusy(false);
    }
  };

  const signIn = () => { window.location.href = '/login'; };

  const startSubscribe = async (planId) => {
    setBusy(true);
    try {
      const applyFounding = billingMode === 'standard' && founding.active && cadence === 'monthly' && (founding.eligible_tiers || []).includes(planId);
      const r = await subscriptionsApi.checkout(planId, cadence, window.location.origin, false, applyFounding ? founding.code : '', billingMode);
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start subscription.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="max-w-lg gap-0 p-0 overflow-hidden"
        data-testid="paywall-dialog"
      >
        <DialogHeader className="border-b border-border bg-gradient-to-r from-primary/15 via-primary/5 to-transparent px-5 py-4">
          <DialogTitle className="flex items-center gap-2 text-[15px] font-semibold">
            <ShieldCheck className="h-4 w-4 text-primary" />
            {paywall?.guest
              ? 'Sign in to continue'
              : paywall?.extra?.free_tier_upgrade
                ? 'Your free writing credit is used up'
                : 'Wallet top-up needed'}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {paywall?.extra?.free_tier_upgrade ? (
              <span>
                The free tier includes enough AI credit for <b>3–5 chapters</b>.
                Your allowance has been used — subscribe to a paid plan to keep writing
                without interruption. Your manuscript stays safe either way.
              </span>
            ) : (
              <>
                To {paywall?.action} you need{' '}
                <b className="font-mono-stat text-primary">{(paywall?.required ?? 0).toLocaleString()}</b>{' '}
                {isCopyscape ? 'Copyscape tokens' : 'tokens'}. Current balance:{' '}
                <b className="font-mono-stat">{(paywall?.available ?? 0).toLocaleString()}</b>.
                {paywall?.monthly_cap_usd != null && (
                  <span className="block mt-1 text-[11px] text-muted-foreground">
                    Monthly project budget: ${paywall?.monthly_spent_usd} of ${paywall?.monthly_cap_usd}.
                  </span>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border bg-panel-header/40 px-3 py-1.5 text-[11.5px]">
          <button
            data-testid="paywall-tab-topup"
            onClick={() => setTab('topup')}
            className={cn('rounded-sm px-2.5 py-1', tab === 'topup' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
          >
            <Coins className="inline h-3 w-3 mr-1" /> Top up
          </button>
          {paywall.can_subscribe && (
            <button
              data-testid="paywall-tab-subscribe"
              onClick={() => setTab('subscribe')}
              className={cn('rounded-sm px-2.5 py-1', tab === 'subscribe' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
            >
              <Crown className="inline h-3 w-3 mr-1" /> Subscribe
            </button>
          )}
          {(paywall.guest || paywall.can_signup) && (
            <button
              data-testid="paywall-tab-signin"
              onClick={() => setTab('signin')}
              className={cn('rounded-sm px-2.5 py-1', tab === 'signin' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
            >
              <LogIn className="inline h-3 w-3 mr-1" /> Sign in
            </button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose} className="ml-auto h-6 w-6 p-0" data-testid="paywall-close">
            <X className="h-3 w-3" />
          </Button>
        </div>

        {/* Body — scrollable so all tier options are reachable */}
        <div className="max-h-[58vh] overflow-y-auto px-4 py-3" data-testid={`paywall-tab-body-${tab}`}>
          {tab === 'topup' && (
            <div className="space-y-2">
              {/* 2026-10 — BYOK setup nudge: shown when capability_gate's
                  enforce_llm_spend_cap flags the user as BYOK-eligible
                  (Premier/Studio/Pro) with no personal key configured
                  yet — a free alternative to buying tokens. */}
              {paywall.byok_nudge && (
                <button
                  type="button"
                  onClick={() => {
                    onClose?.();
                    window.dispatchEvent(new CustomEvent('loom:open-settings', {
                      detail: { tab: 'billing' },
                    }));
                  }}
                  data-testid="paywall-byok-nudge"
                  className="group flex w-full items-start gap-2.5 rounded-md border border-success/40 bg-success/5 p-3 text-left transition hover:border-success hover:bg-success/10"
                >
                  <div className="flex h-8 w-8 flex-none items-center justify-center rounded-sm bg-success/15 text-success">
                    <KeyRound className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold text-success">
                      Skip buying tokens — add your own key
                    </div>
                    <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                      Your plan supports bring-your-own-key. Add a personal OpenAI, Anthropic, or
                      Gemini key in Settings → Billing and keep writing on your own usage, free.
                    </div>
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 flex-none text-success group-hover:translate-x-0.5" />
                </button>
              )}
              {paywall.top_up_options?.map((opt, i) => (
                <button
                  key={i}
                  disabled={busy}
                  onClick={() => startCheckout(opt)}
                  data-testid={`paywall-topup-option-${i}`}
                  className="group w-full rounded-md border border-border bg-card p-3 text-left transition hover:border-primary hover:bg-primary/5 disabled:opacity-50"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-4 w-4 text-primary" />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold">
                        {opt.label || `${opt.tokens.toLocaleString()} ${isCopyscape ? 'Copyscape tokens' : 'tokens'}`}
                      </div>
                      <div className="font-mono-stat text-[11px] text-muted-foreground">
                        {opt.tokens.toLocaleString()} {isCopyscape ? 'CS' : 'tok'} · {format(opt.price_cents)}
                      </div>
                    </div>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />}
                  </div>
                </button>
              ))}
            </div>
          )}

          {tab === 'subscribe' && (
            <div className="space-y-3">
              {/* Cadence toggle */}
              <div className="flex items-center justify-center gap-1 rounded-md border border-border bg-panel-header/40 p-1 text-[11px]">
                <button
                  onClick={() => setCadence('monthly')}
                  data-testid="paywall-cadence-monthly"
                  className={cn('flex-1 rounded-sm px-2 py-1', cadence === 'monthly' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setCadence('yearly')}
                  data-testid="paywall-cadence-yearly"
                  className={cn('flex-1 rounded-sm px-2 py-1 inline-flex items-center justify-center gap-1.5', cadence === 'yearly' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
                >
                  Yearly
                  {promo.enabled && promo.label && (
                    <span data-testid="paywall-yearly-promo-badge"
                          className={cn('rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider',
                            cadence === 'yearly' ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-success/15 text-success')}>
                      {promo.label}
                    </span>
                  )}
                </button>
              </div>

              {/* Standard / BYOK billing mode toggle */}
              <div className="flex items-center gap-1 rounded-md border border-border bg-panel-header/40 p-1 text-[11px]" data-testid="paywall-billing-mode-toggle">
                <button
                  onClick={() => setBillingMode('standard')}
                  data-testid="paywall-billing-mode-standard"
                  className={cn('flex-1 rounded-sm px-2 py-1', billingMode === 'standard' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}
                >
                  Standard
                </button>
                <button
                  onClick={() => setBillingMode('byok')}
                  data-testid="paywall-billing-mode-byok"
                  className={cn(
                    'flex-1 rounded-sm px-2 py-1 inline-flex items-center justify-center gap-1',
                    billingMode === 'byok' ? 'bg-amber-600 text-white' : 'hover:bg-muted',
                  )}
                >
                  <KeyRound className="h-3 w-3" /> BYOK — up to 58% off
                </button>
              </div>
              {billingMode === 'byok' && (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[10.5px] text-amber-800" data-testid="paywall-byok-notice">
                  BYOK prices apply. Paste your OpenAI/Anthropic/Gemini key in Settings → Billing after checkout.
                </p>
              )}

              {plans.length === 0 ? (
                <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center text-[12px] text-muted-foreground">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  <p className="mt-1">Loading plans…</p>
                </div>
              ) : (
                <>
                {/* Feb-2026 — Author vs Publisher savings nudge. Shown only
                    when both tiers are in the plan set and cadence is
                    monthly. Fires the "You could save $65/mo — try Author"
                    banner so visitors comparing Author → Publisher can see
                    the more affordable entry tier first. */}
                {(() => {
                  if (cadence !== 'monthly') return null;
                  const author    = plans.find((p) => p.id === 'author');
                  const publisher = plans.find((p) => p.id === 'publisher');
                  if (!author || !publisher) return null;
                  const saving = Math.max(0,
                    Math.round(publisher.monthly.price_usd - author.monthly.price_usd));
                  if (saving <= 0) return null;
                  return (
                    <ProPlusBanner
                      saving={saving}
                      busy={busy}
                      onPick={() => {
                        // Track the click BEFORE we redirect to Stripe.
                        try {
                          fetch(`${(process.env.REACT_APP_BACKEND_URL || '') + '/api'}/analytics/paywall-event`, {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ event: 'author_banner_click',
                                                    tier: 'author',
                                                    surface: 'paywall' }),
                          }).catch(() => {});
                        } catch { /* ignore */ }
                        startSubscribe('author');
                      }}
                    />
                  );
                })()}
                <div className="grid grid-cols-1 gap-2">
                  {plans.map((p) => {
                    // BYOK mode: show the discounted BYOK price when the plan supports it.
                    const byokKey = cadence === 'monthly' ? 'byok_monthly_price_usd' : 'byok_yearly_price_usd';
                    const isByokPlan = billingMode === 'byok' && p.byok && p[byokKey] != null;
                    const price  = isByokPlan ? p[byokKey] : (p[cadence]?.price_usd ?? 0);
                    const tokens = p[cadence]?.tokens ?? 0;
                    return (
                      <button
                        key={p.id}
                        disabled={busy}
                        onClick={() => startSubscribe(p.id)}
                        data-testid={`paywall-plan-${p.id}`}
                        className={cn(
                          'group w-full rounded-md border bg-card p-3 text-left transition hover:border-primary hover:bg-primary/5 disabled:opacity-50',
                          p.best ? 'border-primary/70 ring-1 ring-primary/30' : 'border-border'
                        )}
                      >
                        <div className="flex items-baseline justify-between">
                          <div className="flex items-center gap-2">
                            <Crown className={cn('h-3.5 w-3.5', p.best ? 'text-primary' : 'text-muted-foreground')} />
                            <span className="text-[13px] font-semibold">{p.name}</span>
                            {p.best && <span className="rounded-sm bg-primary/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary">Best</span>}
                            {isByokPlan && (
                              <span
                                data-testid={`paywall-plan-byok-badge-${p.id}`}
                                className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-700"
                              >
                                BYOK
                              </span>
                            )}
                            {!isByokPlan && founding.active && cadence === 'monthly' && (founding.eligible_tiers || []).includes(p.id) && (
                              <span
                                data-testid={`paywall-founding-badge-${p.id}`}
                                className="rounded-sm bg-success/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-success"
                                title={`Founding Member: ${founding.percent_off}% off for ${founding.months} months · ${founding.redemptions_remaining} spots left · ends Dec 16`}
                              >
                                −{founding.percent_off}% × {founding.months}mo
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-mono-stat text-[14px] font-semibold">{format(Math.round(price * 100))}</div>
                            {isByokPlan && (
                              <div className="font-mono-stat text-[10px] text-neutral-400 line-through">
                                {format(Math.round((p[cadence]?.price_usd ?? 0) * 100))}
                              </div>
                            )}
                            <div className="font-mono-stat text-[10px] text-muted-foreground">/{cadence === 'monthly' ? 'mo' : 'yr'}</div>
                          </div>
                        </div>
                        <div className="mt-1 text-[11px] text-muted-foreground">{p.tagline}</div>
                        {!isByokPlan && founding.active && cadence === 'monthly' && (founding.eligible_tiers || []).includes(p.id) && (
                          <div
                            data-testid={`paywall-founding-spots-${p.id}`}
                            className={cn(
                              'mt-1 text-[10.5px] font-semibold',
                              founding.redemptions_remaining <= 10 ? 'text-destructive' : 'text-success'
                            )}
                          >
                            Only {founding.redemptions_remaining} founding {founding.redemptions_remaining === 1 ? 'spot' : 'spots'} left
                            {founding.redeem_by && (
                              <> — ends {new Date(founding.redeem_by).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</>
                            )}
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-2 text-[10.5px] text-muted-foreground">
                          <Coins className="h-3 w-3" />
                          <span className="font-mono-stat">{tokens.toLocaleString()} tok</span>
                          {p.copyscape_included > 0 && (
                            <>
                              <span>·</span>
                              <ShieldCheck className="h-3 w-3" />
                              <span className="font-mono-stat">{p.copyscape_included} CS scans</span>
                            </>
                          )}
                        </div>
                        {p.features && (
                          <ul className="mt-2 space-y-0.5 text-[10.5px] text-muted-foreground">
                            {p.features.slice(0, 3).map((f) => (
                              <li key={`feat-${String(f).slice(0, 32)}`} className="flex items-center gap-1.5">
                                <Check className="h-2.5 w-2.5 text-success" /> {f}
                              </li>
                            ))}
                          </ul>
                        )}
                      </button>
                    );
                  })}
                </div>
                </>
              )}
              <div className="text-center text-[10px] text-muted-foreground">
                Cancel anytime from <b>Settings → My Account → Manage subscription</b>.
              </div>
            </div>
          )}

          {tab === 'signin' && (
            <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center">
              <LogIn className="mx-auto h-5 w-5 text-primary" />
              <p className="mt-1.5 text-[12px] text-muted-foreground">
                Create a free account first — writing, editing, and version history stay free.
              </p>
              <Button
                size="sm"
                className="mt-3 h-8 gap-1.5"
                onClick={signIn}
                data-testid="paywall-signin-btn"
              >
                <LogIn className="h-3.5 w-3.5" /> Continue with Google
              </Button>
            </div>
          )}
        </div>

        <div className="border-t border-border bg-panel-header/30 px-4 py-2 text-[10.5px] text-muted-foreground">
          Writing, editing, version history and exports stay free. You're only charged when an external API is invoked.
        </div>
        {/* iter-promo — collapsible tier feature comparison */}
        <div className="px-4 pb-4 pt-2 border-t border-zinc-800/40">
          <TierFeatureMap adminMode={false} initiallyOpen={false} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
